/**
 * DTOs shared across endpoint families (`docs/design/00-contracts.md` §13.3).
 *
 * Principle 3: no endpoint ever returns a raw database row. Every response is produced by an
 * explicit `toDto(row)` and validated against the schema declared here, so a new column has to be
 * exposed on purpose.
 */
import { z } from 'zod';

/** `GET /health/live` and `GET /health/ready`. Public, outside `/v1`, touches no tenant tables. */
export const healthResponseSchema = z
  .object({
    status: z.enum(['ok', 'degraded']),
    version: z.string(),
    checks: z.record(z.string(), z.enum(['ok', 'fail'])),
  })
  .meta({ id: 'HealthResponse' });

export type HealthResponse = z.infer<typeof healthResponseSchema>;

/**
 * `GET /health/ip` — the address the rate limiter is counting, so `TRUST_PROXY_HOPS` can be
 * checked after a deploy instead of assumed. Wrong in either direction is silent (sak 58).
 */
export const clientAddressSchema = z
  .object({
    ip: z.string().nullable(),
    trustProxyHops: z.number().int().nonnegative(),
  })
  .meta({ id: 'ClientAddress' });

export type ClientAddress = z.infer<typeof clientAddressSchema>;

/**
 * A boolean off a query string, where everything arrives as text.
 *
 * `?flag` with no value is the shape a checkbox-driven client produces and means the same as
 * `?flag=true`; anything that is not one of the four recognised spellings falls through to
 * `z.boolean()` and is a 400 naming the parameter, never a silent `false`.
 *
 * It lives here rather than in one endpoint family because the Inbox and the notification feed
 * both offer an `unread` filter, and two copies of a coercion rule are two chances for
 * `?unread` with no value to mean different things on two pages (CLAUDE.md: "if you are writing
 * the second copy of one of these, stop"). Deliberately NOT exported through the barrel: it is
 * a building block of contracts, not a contract.
 */
export const booleanQuerySchema = z.preprocess((value) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value;
  const text = String(value).trim().toLowerCase();
  if (text === 'true' || text === '1' || text === '') return true;
  if (text === 'false' || text === '0') return false;
  return value;
}, z.boolean());
